import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Flame, CheckCircle2, Target, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AnalyticsPage({ token }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/analytics/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics');
    }
  }, [token]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (!analytics) {
    return (
      <div className="min-h-screen bg-background" data-testid="analytics-loading">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6 sm:py-12">
          {/* Functional Back Button (allows user to leave if loading is slow) */}
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="mb-6 sm:mb-8 rounded-full text-sm"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('nav.back')}
          </Button>

          {/* Title & Subtitle Skeleton */}
          <div className="text-center mb-10 sm:mb-16 space-y-3 sm:space-y-4">
            <div className="h-10 sm:h-12 w-64 bg-muted rounded-lg animate-pulse mx-auto" />
            <div className="h-4 w-96 max-w-full bg-muted rounded animate-pulse mx-auto" />
          </div>

          {/* Stats Grid Skeleton (4 Cards) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="bg-card/50 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-border space-y-4 animate-pulse"
              >
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-muted rounded-full" />
                <div className="h-8 w-24 bg-muted rounded" />
                <div className="h-4 w-32 bg-muted rounded" />
                <div className="h-3 w-40 max-w-full bg-muted rounded" />
              </div>
            ))}
          </div>

          {/* Summary Cards Skeleton (3 Cards) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 mb-10 sm:mb-16">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="bg-muted/10 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-border space-y-4 animate-pulse"
              >
                <div className="h-6 w-32 bg-muted rounded" />
                <div className="h-12 w-16 bg-muted rounded" />
                <div className="h-3 w-40 max-w-full bg-muted rounded" />
              </div>
            ))}
          </div>

          {/* Motivation Card Skeleton */}
          <div className="bg-card/30 rounded-2xl p-8 sm:p-12 border border-border animate-pulse flex justify-center">
            <div className="h-6 w-3/4 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  const stats = [
    {
      icon: Flame,
      label: t('analytics.streaks.active'),
      value: analytics.active_streaks,
      detail: t('analytics.streaks.longest', { count: analytics.max_streak }),
      color: 'text-accent'
    },
    {
      icon: CheckCircle2,
      label: t('analytics.tasks.completed'),
      value: analytics.completed_todos,
      detail: t('analytics.tasks.rate', { rate: analytics.completion_rate }),
      color: 'text-accent'
    },
    {
      icon: Target,
      label: t('analytics.wants.progress'),
      value: `${analytics.avg_want_progress}%`,
      detail: t('analytics.wants.completed', { count: analytics.completed_wants }),
      color: 'text-primary'
    },
    {
      icon: BookOpen,
      label: t('analytics.reflections'),
      value: analytics.total_reflections,
      detail: t('analytics.reflections.entries'),
      color: 'text-muted-foreground'
    }
  ];

  const getMotivationMessage = () => {
    if (analytics.completion_rate > 75) return t('analytics.motivation.high');
    if (analytics.completion_rate > 50) return t('analytics.motivation.medium');
    return t('analytics.motivation.low');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="analytics-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6 sm:py-12">
        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6 sm:mb-8 rounded-full text-sm"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('nav.back')}
        </Button>

        <div className="text-center mb-10 sm:mb-16 space-y-3 sm:space-y-4">
          <h1 className="font-serif text-3xl sm:text-5xl font-light tracking-tight" data-testid="analytics-title">
            {t('analytics.title')}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
            {t('analytics.subtitle')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-card/50 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-border hover:shadow-lg transition-all duration-300"
              data-testid={`stat-card-${index}`}
            >
              <stat.icon className={`w-6 h-6 sm:w-8 sm:h-8 ${stat.color} mb-3 sm:mb-4`} />
              <p className="text-3xl sm:text-4xl font-semibold mb-2">{stat.value}</p>
              <p className="text-xs sm:text-sm font-medium text-foreground mb-1">{stat.label}</p>
              <p className="text-xs text-muted-foreground">{stat.detail}</p>
            </div>
          ))}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 mb-10 sm:mb-16">
          <div className="bg-muted/30 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-border" data-testid="beings-summary">
            <h3 className="font-serif text-xl sm:text-2xl font-light mb-3 sm:mb-4">{t('analytics.being.label')}</h3>
            <p className="text-4xl sm:text-5xl font-semibold text-primary mb-2">{analytics.total_beings}</p>
            <p className="text-xs sm:text-sm text-muted-foreground">{t('analytics.being.subtitle')}</p>
          </div>

          <div className="bg-muted/30 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-border" data-testid="wants-summary">
            <h3 className="font-serif text-xl sm:text-2xl font-light mb-3 sm:mb-4">{t('analytics.want.label')}</h3>
            <p className="text-4xl sm:text-5xl font-semibold text-accent mb-2">{analytics.total_wants}</p>
            <p className="text-xs sm:text-sm text-muted-foreground">{t('analytics.want.subtitle')}</p>
          </div>

          <div className="bg-muted/30 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-border" data-testid="todos-summary">
            <h3 className="font-serif text-xl sm:text-2xl font-light mb-3 sm:mb-4">{t('analytics.todo.label')}</h3>
            <p className="text-4xl sm:text-5xl font-semibold text-foreground mb-2">{analytics.total_todos}</p>
            <p className="text-xs sm:text-sm text-muted-foreground">{t('analytics.todo.subtitle')}</p>
          </div>
        </div>

        {/* Motivation Message */}
        <div className="glass-card rounded-2xl p-8 sm:p-12 text-center border border-border" data-testid="motivation-card">
          <p className="font-serif text-lg sm:text-2xl text-foreground/90 leading-relaxed">
            {getMotivationMessage()}
          </p>
        </div>
      </div>
    </div>
  );
}
